import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { APP_ICONS } from '../../../icons/icon-provider';
import { DashboardService } from '../../../services/dashboard.service';
import { JobCreationTriggerService } from '../../../services/job-creation-trigger.service';
import { SlidePanelService } from '../../../services/slide-panel.service';
import { GigsumoConstants } from '../../../services/GigsumoConstants';
import { FormValidation } from '../../../services/FormValidation';
import { JobService } from '../../../services/job.service';
import { SkeletonListComponent } from '../../../shared/ui/skeleton/skeleton-list.component';
import { DashIconComponent } from '../shared/dash-icon.component';
import { JobCardComponent } from '../../../components/cards/job-card/job-card.component';
import { TimezoneDatePipe } from '../../../timezone-date.pipe';
import {
  FunnelRateCell,
  FunnelRow,
  SlotsBlock,
  SpotlightJobRow,
  SpotlightJobVariant,
  ThisWeekView,
  deltaFromChange,
  formatLongDate,
  getgreeting,
  mapActiveCandidateRow,
  mapBenchFunnel,
  mapBenchOfferRow,
  mapJobCandidateSlots,
  mapOfferStages,
  mapRecentSubmissionRow,
  mapSpotlightJob,
  mapThisWeekSummary,
  mapTodayActivity,
  mapWeeklyMetrics,
} from '../shared/dashboard-mappers';
import { BENCH_MOCK } from './mock-data';
import { CompactCountPipe } from '../../../pipe/compact-count.pipe';

@Component({
  selector: 'app-bench-sales-dashboard',
  standalone: true,
  imports: [CommonModule, NgIconComponent, DashIconComponent, SkeletonListComponent, JobCardComponent,TimezoneDatePipe, CompactCountPipe],
  providers: [provideIcons(APP_ICONS)],
  templateUrl: './bench-sales-dashboard.component.html',
  styleUrls: ['./bench-sales-dashboard.component.scss']
})
export class BenchSalesDashboardComponent implements OnInit, OnDestroy {
  /** Mock still backs Quick Actions + AI Insights (kept per product). Everything else is live. */
  readonly data = BENCH_MOCK;

  /** Hidden `<app-job-card>` instance whose only purpose is to expose its
   *  `apply()` method and the `#candidateSupplyTemplate` ViewChild so the
   *  Top Job Spotlight Apply button can open the same candidate-supply
   *  modal used from `/newcandidates`. The embedded job-card renders no
   *  visible block (unknown `viewSize` → every `*ngIf="viewSize == 'x'"`
   *  branch is false) — only its top-level `<ng-template>` refs load. */
  @ViewChild('spotlightJobCardRef') spotlightJobCardRef: JobCardComponent;

  // ── Welcome banner (live) ─────────────────────────────────────────
  // Seed from localStorage (persisted at login) so the greeting name paints
  // instantly; fetchCurrentUser() then refreshes it from the API.
  userFirstName = (localStorage.getItem('firstName') || '').trim();
  // readonly todayFormatted = formatLongDate();
  currentTz = localStorage.getItem('timezonedate') || '';
 
  get todayFormatted(): string {
    return formatLongDate(new Date(), this.currentTz);
  }
  
  get greeting(): string {
    return getgreeting(new Date(), this.currentTz);
  }

  // ── Job & Candidate Slots (live from /dashboard/job-candidate-slots) ─
  slots: SlotsBlock = { used: 0, open: 0, allocated: 0, totalPosted: 0, plan: '' };
  loadingSlots = false;

  // ── This Week summary (live from /dashboard/this-week-summary) ─────
  thisWeek: ThisWeekView = { range: '', rows: [] };
  loadingThisWeek = false;

  // ── Active Candidates list (live from candidates/filter, paginated) ─
  activeCandidates: any[] = [];
  activeCandidatesCursor: any[] | null = null;
  activeCandidatesHasMore = false;
  activeCandidatesTotalCount = 0;
  loadingActiveCandidates = false;
  loadingMoreActiveCandidates = false;

  // ── Weekly Activity Metrics (live) ─────────────────────────────────
  weeklyMetrics: { range: string; goals: any[] } | null = null;
  loadingWeekly = false;

  // ── Offer Pipeline (live, paginated via "Load more") ──────────────
  offerStages: any[] = [];
  offers: any[] = [];
  offersCursor: any[] | null = null;
  offersHasMore = false;
  offersTotalCount = 0;
  loadingOffers = false;
  loadingMoreOffers = false;

  // ── Recent Submissions (live, paginated via "Load more") ──────────
  recentSubmissions: any[] = [];
  submissionsCursor: any[] | null = null;
  submissionsHasMore = false;
  submissionsTotalCount = 0;
  loadingSubmissions = false;
  loadingMoreSubmissions = false;

  // ── Candidate Pipeline Stats (business-scope) ─────────────────────
  /** Keyed by KPI label; template falls back to mock value when absent. */
  kpiOverrides: Record<string, string | number> = {};
  /** Live trend pill per KPI tile. Populated from This Week Summary
   *  v1.0.252 response fields `activeCandidatesStats` (week-over-week)
   *  and `submissionsStats` (month-over-month). Keyed by KPI label so
   *  the template override below the mock trend is a 1-line addition. */
  kpiTrendOverrides: Record<string, { text: string; dir: 'up' | 'down' | 'flat' }> = {};
  /** Live submission funnel rows; null → template falls back to mock. */
  submissionFunnelRows: FunnelRow[] | null = null;
  /** Live funnel rates; null → template falls back to mock. */
  submissionFunnelRates: FunnelRateCell[] | null = null;
  loadingCandidateStats = false;

  // ── Today's Activity (business-scope, paginated via "Load more") ──
  todayActivity: any[] = [];
  todayActivityCursor: any[] | null = null;
  todayActivityHasMore = false;
  todayActivityTotalCount = 0;
  loadingTodayActivity = false;
  loadingMoreTodayActivity = false;

  // ── Top Job Spotlight (driven by whichever Active Candidate is selected) ──
  private readonly JOB_SPOTLIGHT_PAGE_SIZE = 5;

  activeJobSpotlightTab: SpotlightJobVariant = 'applied';
  selectedCandidateId: string | null = null;
  selectedCandidateName = '';

  spotlightJobsApplied: SpotlightJobRow[] = [];
  spotlightJobsMarket:  SpotlightJobRow[] = [];
  spotlightJobsAppliedTotal = 0;
  spotlightJobsMarketTotal  = 0;
  loadingSpotlightJobsApplied = false;
  loadingSpotlightJobsMarket  = false;
  loadingMoreSpotlightJobsApplied = false;
  loadingMoreSpotlightJobsMarket  = false;

  private spotlightJobsAppliedCursor: any = null;
  private spotlightJobsMarketCursor:  any = null;
  spotlightJobsAppliedHasMore = false;
  spotlightJobsMarketHasMore  = false;

  get jobSpotlightRows(): SpotlightJobRow[] {
    return this.activeJobSpotlightTab === 'applied'
      ? this.spotlightJobsApplied
      : this.spotlightJobsMarket;
  }
  get jobSpotlightLoading(): boolean {
    return this.activeJobSpotlightTab === 'applied'
      ? this.loadingSpotlightJobsApplied
      : this.loadingSpotlightJobsMarket;
  }
  get jobSpotlightHasMore(): boolean {
    return this.activeJobSpotlightTab === 'applied'
      ? this.spotlightJobsAppliedHasMore
      : this.spotlightJobsMarketHasMore;
  }
  get jobSpotlightLoadingMore(): boolean {
    return this.activeJobSpotlightTab === 'applied'
      ? this.loadingMoreSpotlightJobsApplied
      : this.loadingMoreSpotlightJobsMarket;
  }
  jobSpotlightTabCount(id: SpotlightJobVariant): number {
    return id === 'applied' ? this.spotlightJobsAppliedTotal : this.spotlightJobsMarketTotal;
  }

  selectJobSpotlight(id: SpotlightJobVariant) { this.activeJobSpotlightTab = id; }

  loadMoreJobSpotlight() {
    if (!this.selectedCandidateId || this.jobSpotlightLoadingMore || !this.jobSpotlightHasMore) return;
    if (this.activeJobSpotlightTab === 'applied') {
      this.fetchSpotlightJobsApplied(this.selectedCandidateId, false);
    } else {
      this.fetchSpotlightJobsMarket(this.selectedCandidateId, false);
    }
  }

  selectActiveCandidate(c: any) {
    if (!c?.id || c.id === this.selectedCandidateId) return;
    this.selectedCandidateId = c.id;
    this.selectedCandidateName = c.name || '';
    // Reset both caches and totals, then fire both fetches so the tab badges
    // show real counts immediately.
    this.spotlightJobsApplied = [];
    this.spotlightJobsMarket  = [];
    this.spotlightJobsAppliedTotal = 0;
    this.spotlightJobsMarketTotal  = 0;
    this.spotlightJobsAppliedCursor = null;
    this.spotlightJobsMarketCursor  = null;
    this.spotlightJobsAppliedHasMore = false;
    this.spotlightJobsMarketHasMore  = false;
    this.fetchSpotlightJobsApplied(c.id, true);
    this.fetchSpotlightJobsMarket(c.id, true);
  }

  private unwrapList(res: any): any | null {
    if (!res) return null;
    const ok =
      res.code === '00000' ||
      res.status === 'SUCCESS' ||
      Array.isArray(res.jobList) ||
      (res.data && Array.isArray(res.data.jobList));
    if (!ok) return null;
    return res.data ?? res;
  }

  private pickTotal(data: any, list: any[]): number {
    if (data) {
      const cands = [data.totalCount, data.totalJobsCount, data.totalMatchCount, data.totalElements, data.total];
      for (const v of cands) {
        if (v != null && v !== '' && !isNaN(Number(v))) return Number(v);
      }
    }
    return list.length;
  }

  private fetchSpotlightJobsApplied(candidateId: string, reset: boolean) {
    if (reset) this.loadingSpotlightJobsApplied = true;
    else this.loadingMoreSpotlightJobsApplied = true;
    const cursor = reset ? null : this.spotlightJobsAppliedCursor;

    this.dash.findAppliedJobs(candidateId, cursor)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (candidateId !== this.selectedCandidateId) return;  // discard stale
          const body = this.unwrapList(res);
          if (body) {
            const list = body.jobList || [];
            const startRank = reset ? 0 : this.spotlightJobsApplied.length;
            const mapped = list.map((j: any, i: number) =>
              mapSpotlightJob(j, startRank + i + 1, 'applied'));
                  const limited = mapped.slice(0, 5); // Only take first 5
          this.spotlightJobsApplied = reset ? limited : [...this.spotlightJobsApplied, ...limited];
            // this.spotlightJobsApplied = reset ? mapped : [...this.spotlightJobsApplied, ...mapped];
            if (reset) this.spotlightJobsAppliedTotal = this.pickTotal(body, list);
            this.spotlightJobsAppliedCursor = body.searchAfterKey ?? null;
            this.spotlightJobsAppliedHasMore = !!this.spotlightJobsAppliedCursor && list.length > 0;
          }
          if (reset) this.loadingSpotlightJobsApplied = false;
          else this.loadingMoreSpotlightJobsApplied = false;
        },
        error: () => {
          if (reset) this.loadingSpotlightJobsApplied = false;
          else this.loadingMoreSpotlightJobsApplied = false;
        },
      });
  }

  private fetchSpotlightJobsMarket(candidateId: string, reset: boolean) {
    if (reset) this.loadingSpotlightJobsMarket = true;
    else this.loadingMoreSpotlightJobsMarket = true;
    const cursor = reset ? null : this.spotlightJobsMarketCursor;

    this.dash.findTopSuggestedJobs(candidateId, this.JOB_SPOTLIGHT_PAGE_SIZE, cursor)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (candidateId !== this.selectedCandidateId) return;
          const body = this.unwrapList(res);
          if (body) {
            const list = body.jobList || [];
            const startRank = reset ? 0 : this.spotlightJobsMarket.length;
            const mapped = list.map((j: any, i: number) =>
              mapSpotlightJob(j, startRank + i + 1, 'market'));
            this.spotlightJobsMarket = reset ? mapped : [...this.spotlightJobsMarket, ...mapped];
            if (reset) this.spotlightJobsMarketTotal = this.pickTotal(body, list);
            this.spotlightJobsMarketCursor = body.searchAfterKey ?? null;
            this.spotlightJobsMarketHasMore = !!this.spotlightJobsMarketCursor && list.length > 0;
          }
          if (reset) this.loadingSpotlightJobsMarket = false;
          else this.loadingMoreSpotlightJobsMarket = false;
        },
        error: () => {
          if (reset) this.loadingSpotlightJobsMarket = false;
          else this.loadingMoreSpotlightJobsMarket = false;
        },
      });
  }

  private destroy$ = new Subject<void>();

  constructor(
    private dash: DashboardService,
    private router: Router,
    private jobCreationTrigger: JobCreationTriggerService,
    private slidePanel: SlidePanelService,
    private formValidation: FormValidation,
    private jobService: JobService,
  ) {}

  /** Avatar fallback colours for offers without a candidate photo — same
   *  name-hash palette the Candidates page uses, via the shared JobService. */
  getAvatarColor(name: string): string { return this.jobService.getColor(name || ''); }
  getAvatarTextColor(name: string): string { return this.jobService.getFullnameTextColor(name || ''); }

  /** Recent Submissions badge colour — sourced from FormValidation's
   *  APPLICANT_STATUS_COLOR (single source of truth for status colours),
   *  keyed on the raw backend status. Falls back to an empty style when the
   *  status has no entry, so an unknown status renders as a plain pill. */
  stageStyle(statusRaw: string): { color: string; background: string } {
    return this.formValidation.getStyle(statusRaw as any) as any;
  }

  // Delegates to the landing-page nav-bar's existing create-candidate flow
  // (plan check, org-setup gate, modal). Nav-bar is guaranteed mounted on
  // /landingPage/bench.
  onAddNewCandidate(): void {
    this.jobCreationTrigger.trigger('CANDIDATE');
  }

  goToPlanQuota() { this.router.navigate(['/plan-quota']); }

  /** Full Bench (My Bench) page, pre-filtered to this user's own candidates. */
  goToMyBench() {
    this.router.navigate(['/newcandidates'], { queryParams: { source: 'myCandidates' } });
  }

  /** Active Candidates card header. Super-admins manage the whole business in
   *  the Talent Center — except on the Free plan, where super-admin scope
   *  doesn't apply, so everyone goes to their own bench. */
  goToActiveCandidatesList() {
    const isFreePlan = localStorage.getItem('MemberShipType') === GigsumoConstants.FREE_PLAN;
    if (!isFreePlan && localStorage.getItem('businessRole') === 'SUPERADMIN') {
      this.router.navigate(['/talent-center']);
      return;
    }
    this.goToMyBench();
  }

  /** Open the currently-selected candidate on the newcandidates page with the
   *  tab that matches the active Top Job Spotlight tab pre-selected. */
  private readonly JOB_SPOTLIGHT_TAB_ROUTES: Record<SpotlightJobVariant, string> = {
    applied: 'jobsApplied',
    market:  'suggestedJobs',
  };

  goToJobSpotlightTab() {
    if (!this.selectedCandidateId) return;
    this.router.navigate(['/newcandidates'], {
      queryParams: {
        candidateId: this.selectedCandidateId,
        tabName: this.JOB_SPOTLIGHT_TAB_ROUTES[this.activeJobSpotlightTab],
        source: 'myCandidates',
      },
    });
  }

  /** Open the V3 job profile slider on the AI Insights tab for the clicked
   *  Top Job Spotlight row. Mirrors the Match-badge click on the listings
   *  (`job-card.component.ts → openMatch`). `JOB_SPOTLIGHT_TAB_ROUTES`
   *  already uses the `'jobsApplied' / 'suggestedJobs'` values that
   *  `ai-criteria.util.ts:getRankingPayload()` understands. */
  openJobSpotlightMatch(row: SpotlightJobRow, ev?: Event) {
    ev?.stopPropagation();
    if (!row?.id || !this.selectedCandidateId) return;
    this.slidePanel.openJobDetail(row.raw ?? { jobId: row.id }, this.selectedCandidateId, {
      variant: 'v3',
      initialTab: 'ai',
      pageFrom: this.JOB_SPOTLIGHT_TAB_ROUTES[this.activeJobSpotlightTab],
    });
  }

  /** Row-action handler.
   *  - `actionPrimary=false` (Applied → "View") opens the V3 job profile
   *    slider on the Profile tab, same as before.
   *  - `actionPrimary=true` (Marketplace → "Apply") delegates to the
   *    hidden `<app-job-card>` instance's `apply()` method, which opens
   *    the candidate-supply modal — the exact same popup a user gets by
   *    tapping Apply on a job-card from `/newcandidates`. */
  openJobSpotlightAction(row: SpotlightJobRow, ev?: Event) {
    ev?.stopPropagation();
    if (!row?.id || !this.selectedCandidateId) return;

    if (row.actionPrimary) {
      if (!this.spotlightJobCardRef) return;
      this.spotlightJobCardRef.apply(
        row.raw,
        undefined,
        this.spotlightJobCardRef.candidateSupplyTemplate,
      );
      return;
    }

    this.slidePanel.openJobDetail(row.raw ?? { jobId: row.id }, this.selectedCandidateId, {
      variant: 'v3',
      initialTab: 'profile',
      pageFrom: this.JOB_SPOTLIGHT_TAB_ROUTES[this.activeJobSpotlightTab],
    });
  }

  ngOnInit() {
    // Welcome-banner name only needs `userId` (from localStorage), not the
    // primed ids — fire it immediately and in parallel so the greeting name
    // isn't delayed behind the primeIds() round-trip.
    this.fetchCurrentUser();
    // Resolve the ids ONCE per load before firing the dashboard API family —
    // see recruiter-dashboard.component.ts ngOnInit for the rationale.
    this.dash.primeIds()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.fetchSlots();
        this.fetchThisWeek();
        this.fetchWeekly();
        this.fetchOffers(true);
        this.fetchSubmissions(true);
        // Pipeline stats + Today's Activity now support USER context per
        // Dashboard_API_Documentation.html §3 — no longer gated on super-admin.
        this.fetchCandidatePipelineStats();
        this.fetchTodayActivity(true);
        // `getActiveCandidates` now branches internally — super-admins get
        // the business-scoped `candidates/filter` payload; regular users get
        // the `/newcandidates?source=myCandidates` payload — so the gate
        // isn't needed here.
        this.fetchActiveCandidates(true);
      });
  }

  private fetchSlots() {
    this.loadingSlots = true;
    this.dash.getJobCandidateSlots('CANDIDATE')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' || res?.code === '00' || res?.data) {
            this.slots = mapJobCandidateSlots(res);
          }
          this.loadingSlots = false;
        },
        error: () => { this.loadingSlots = false; },
      });
  }

  private fetchThisWeek() {
    this.loadingThisWeek = true;
    this.dash.getThisWeekSummary('CANDIDATE')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' || res?.code === '00' || res?.data) {
            const v = mapThisWeekSummary(res, 'bench');
            if (v) this.thisWeek = v;

            // Live KPI trend pills from v1.0.252 fields.
            // Bench Candidates   → `activeCandidatesStats.weekCount` + arrow
            // Submissions (MTD)  → `submissionsStats.monthCount` + arrow
            const summary = res?.data?.summary;
            const ac = summary?.activeCandidatesStats ?? summary?.onBench;
            const ss = summary?.submissionsStats;
            const nextTrends: Record<string, { text: string; dir: 'up' | 'down' | 'flat' }> = {};
            // Big-number tile values now come from this-week-summary too:
            //   Bench Candidates  → activeCandidatesStats.total
            //   Submissions (MTD) → submissionsStats.total
            const nextOverrides: Record<string, string | number> = {};
            if (ac) {
              // Sign is encoded in the pill color (.trend-up green /
              // .trend-down red / .trend-flat gray) via `dir`; the
              // absolute value is what the reader sees.
              const d = deltaFromChange(ac.change);
              nextTrends['Bench Candidates'] = {
                text: `${Math.abs(Number(ac.change ?? 0))} this week`,
                dir: d.dir,
              };
              if (ac.total != null) nextOverrides['Bench Candidates'] = ac.total;
            }
            if (ss) {
              const d = deltaFromChange(ss.change);
              nextTrends['Submissions (MTD)'] = {
                text: `${Math.abs(Number(ss.change ?? 0))} this month`,
                dir: d.dir,
              };
              if (ss.total != null) nextOverrides['Submissions (MTD)'] = ss.total;
            }
            this.kpiTrendOverrides = { ...this.kpiTrendOverrides, ...nextTrends };
            if (Object.keys(nextOverrides).length) {
              this.kpiOverrides = { ...this.kpiOverrides, ...nextOverrides };
            }
          }
          this.loadingThisWeek = false;
        },
        error: () => { this.loadingThisWeek = false; },
      });
  }

  private fetchCurrentUser() {
    this.dash.getCurrentUser()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          const name = res?.firstName ?? res?.data?.firstName ?? res?.data?.user?.firstName ?? '';
          const trimmed = String(name).trim();
          if (trimmed) {
            this.userFirstName = trimmed;
            // Keep localStorage fresh so subsequent visits paint the name
            // instantly, and cover login paths that didn't persist it.
            localStorage.setItem('firstName', trimmed);
          }
        },
        error: () => {},
      });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  donutBackground(): string {
    const { used, allocated } = this.slots;
    const deg = allocated > 0 ? (used / allocated) * 360 : 0;
    return `conic-gradient(var(--gs-purple-light) 0 ${deg}deg, var(--gs-gray-100) ${deg}deg 360deg)`;
  }

  /** stroke-dashoffset for the SVG ring donut "used" arc. See the
   *  matching helper in recruiter-dashboard.component.ts for the math. */
  donutDashoffset(): number {
    const { used, allocated } = this.slots;
    if (allocated <= 0) return 157;
    const ratio = Math.max(0, Math.min(1, used / allocated));
    return 157 * (1 - ratio);
  }

  private fetchWeekly() {
    this.loadingWeekly = true;
    this.dash.benchWeeklyMetrics()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000') {
            this.weeklyMetrics = mapWeeklyMetrics(res.data?.metrics);
          }
          this.loadingWeekly = false;
        },
        error: () => { this.loadingWeekly = false; },
      });
  }

  private fetchOffers(reset: boolean) {
    if (reset) this.loadingOffers = true;
    else this.loadingMoreOffers = true;

    this.dash.benchOfferPipeline({
      searchAfterKey: reset ? null : this.offersCursor,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000') {
            const p = res.data?.pipeline;
            if (p) {
              this.offerStages = mapOfferStages(p.summary);
              const mapped = (p.offers || []).map(mapBenchOfferRow);
              this.offers = reset ? mapped : [...this.offers, ...mapped];
              this.offersCursor = p.searchAfterKey ?? null;
              this.offersHasMore = !!p.hasMore;
              if (reset && p.totalCount != null) this.offersTotalCount = p.totalCount;
            }
          }
          if (reset) this.loadingOffers = false;
          else this.loadingMoreOffers = false;
        },
        error: () => {
          if (reset) this.loadingOffers = false;
          else this.loadingMoreOffers = false;
        },
      });
  }

  loadMoreOffers() {
    if (this.offersHasMore && !this.loadingMoreOffers) this.fetchOffers(false);
  }

  private fetchSubmissions(reset: boolean) {
    if (reset) this.loadingSubmissions = true;
    else this.loadingMoreSubmissions = true;

    this.dash.recentSubmissions({
      searchAfterKey: reset ? null : this.submissionsCursor,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000') {
            const d = res.data;
            const mapped = (d?.submissions || []).map(mapRecentSubmissionRow);
            this.recentSubmissions = reset ? mapped : [...this.recentSubmissions, ...mapped];
            this.submissionsCursor = d?.searchAfterKey ?? null;
            this.submissionsHasMore = !!d?.hasMore;
            if (reset && d?.totalCount != null) this.submissionsTotalCount = d.totalCount;
          }
          if (reset) this.loadingSubmissions = false;
          else this.loadingMoreSubmissions = false;
        },
        error: () => {
          if (reset) this.loadingSubmissions = false;
          else this.loadingMoreSubmissions = false;
        },
      });
  }

  loadMoreSubmissions() {
    if (this.submissionsHasMore && !this.loadingMoreSubmissions) this.fetchSubmissions(false);
  }

  private fetchCandidatePipelineStats() {
    this.loadingCandidateStats = true;
    this.dash.benchCandidatePipelineStats()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          // 2026-04-25: backend now emits `code: 1` / `status: "SUCCESS"`.
          // Accept legacy `'00000'` too for rolling-deploy safety.
          const ok = res?.code === '00000' || res?.code === 1 || res?.status === 'SUCCESS';
          if (ok && res.data) {
            const d = res.data;
            // NOTE: 'Bench Candidates' / 'Submissions (MTD)' tile numbers now
            // come from dashboard/this-week-summary (activeCandidatesStats.total
            // / submissionsStats.total) in fetchThisWeek(). This endpoint only
            // feeds the submission funnel now.
            const f = mapBenchFunnel(d);
            this.submissionFunnelRows = f.rows;
            this.submissionFunnelRates = f.rates;
          }
          this.loadingCandidateStats = false;
        },
        error: () => { this.loadingCandidateStats = false; },
      });
  }

  private fetchTodayActivity(reset: boolean) {
    if (reset) this.loadingTodayActivity = true;
    else this.loadingMoreTodayActivity = true;

    this.dash.benchTodaysActivity({
      searchAfterKey: reset ? null : this.todayActivityCursor,
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' && res.data) {
            const d = res.data;
            const mapped = mapTodayActivity(
              d.events,
              reset ? d.viewSummary : [],
              'bench'
            );
            this.todayActivity = reset ? mapped : [...this.todayActivity, ...mapped];
            this.todayActivityCursor = d.searchAfterKey ?? null;
            this.todayActivityHasMore = !!d.searchAfterKey && (d.events || []).length > 0;
            // Doc §4.3: `totalElements` removed; `eventCount` is now the
            // total across pages (constant, not current page size).
            if (reset && d.eventCount != null) this.todayActivityTotalCount = d.eventCount;
          }
          if (reset) this.loadingTodayActivity = false;
          else this.loadingMoreTodayActivity = false;
        },
        error: () => {
          if (reset) this.loadingTodayActivity = false;
          else this.loadingMoreTodayActivity = false;
        },
      });
  }

  loadMoreTodayActivity() {
    if (this.todayActivityHasMore && !this.loadingMoreTodayActivity) this.fetchTodayActivity(false);
  }

  private fetchActiveCandidates(reset: boolean) {
    if (reset) this.loadingActiveCandidates = true;
    else this.loadingMoreActiveCandidates = true;

    this.dash.getActiveCandidates({
      limit: 5,
      searchAfterKey: reset ? null : this.activeCandidatesCursor,
      status: ['ACTIVE'],
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          if (res?.code === '00000' && res.data) {
            const d = res.data;
            const mapped = (d.candidateList || []).map(mapActiveCandidateRow);
            this.activeCandidates = reset ? mapped : [...this.activeCandidates, ...mapped];
            this.activeCandidatesCursor = d.searchAfterKey ?? null;
            this.activeCandidatesHasMore = !!d.searchAfterKey && (d.candidateList || []).length > 0;
            if (reset && d.totalCandidatesCount != null) this.activeCandidatesTotalCount = d.totalCandidatesCount;
            // Auto-select the first candidate so the Top Job Spotlight has data immediately.
            if (reset && !this.selectedCandidateId && mapped.length) {
              this.selectActiveCandidate(mapped[0]);
            }
          }
          if (reset) this.loadingActiveCandidates = false;
          else this.loadingMoreActiveCandidates = false;
        },
        error: () => {
          if (reset) this.loadingActiveCandidates = false;
          else this.loadingMoreActiveCandidates = false;
        },
      });
  }

  loadMoreActiveCandidates() {
    if (this.activeCandidatesHasMore && !this.loadingMoreActiveCandidates) this.fetchActiveCandidates(false);
  }
}
